import React, { useRef, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';
import { makeGetProject } from 'selectors/projects';
import { makeGetMilestone } from 'selectors/milestones';

import { updateTask, removeTask, moveTask, linkTasks } from 'actions/tasks';
import { tieTasks } from 'actions/goals';
import { showModal } from 'actions/modal';
import { openFlyout, closeFlyout } from 'actions/flyout';

import { duplicateTask } from 'api/tasks-api';

import { trackEvent } from 'services/events-tracker-service';
import { canChangeDueDate, isPersonalTask, isSubtaskTask } from 'utils/task';
import { createSlug, copyUrlToClipboard } from 'utils/index';
import { buildAppUrl } from 'utils/url';
import { createAlert } from 'services/alert-service';
import { useCanUser } from 'shared-hooks/permission';
import { useIsDependant } from 'shared-hooks/task';

import Popout from 'components/popout';
import PopoutListActions from 'components/PopoutListActions';
import PromptPopout from 'components/prompt-popout';
import SelectMilestonePopout from 'components/select-milestone-popout';
import SelectTaskPopout from 'components/select-task-popout';
import TaskDueDatePicker from 'components/TaskDueDatePicker';
import LabelPicker from 'components/label-picker';
import TaskArchiveToggle from 'components/TaskArchiveToggle';
import SelectTaskGroupPopout from 'components/SelectTaskGroupPopout';
import SelectGoalPopout from 'components/SelectGoalPopout';
import Tooltip from 'components/tooltip';

import IconMoreHoriz from 'icons/icon-more-horiz';
import IconClock from 'icons/icon-clock';
import IconEdit from 'icons/icon-edit';
import IconList from 'icons/icon-list';
import IconTask from 'icons/icon-task';
import IconCalendarSimple from 'icons/icon-calendar-simple';
import IconLabel from 'icons/icon-label';
import IconStatus from 'icons/icon-status';
import IconTransfer from 'icons/icon-transfer';
import IconPlus from 'icons/icon-plus';
import IconArchive from 'icons/icon-archive';
import IconRemove from 'icons/icon-remove';
import IconLink from 'icons/icon-link';
import IconGoal from 'icons/icon-goal';
import IconTemplate from 'icons/icon-template';

const DEFAULT_ACTIONS = ['addTimeEntry', 'viewTimeReport', 'editName', 'changeMilestone', 'changeTaskGroup', 'taskDep', 'taskLink', 'tieToGoal', 'dueDate', 'labels', 'duplicate', 'createTemplate', 'convertToTask', 'moveToTask', 'copyURL', 'archive', 'remove'];

const TaskActions = ({ id, className = 'task-actions-holder', innerClassName = 'task-actions-trigger', allowedActions, taskLabels, taskMilestone, taskDescription, taskStartDate, taskDueDate, taskDep, taskName, projectId, taskTaskGroup, taskArchived, taskCreatedBy, taskCompleted, parentTaskId, isPersonal, isSubtask, onEditNameClick, onRemove }) => {
    const { t } = useTranslation(['task', 'labels', 'common', 'time-tracking', 'template']);
    const [visible, setVisible] = useState(false);
    const [innerVisible, setInnerVisible] = useState(false);
    const popoutCtrl = useRef(null);
    const canViewTimeReport = useCanUser('viewTimeReports');
    const canAddTimeEntry = useCanUser('startStopTimer');
    const canEditTask = useCanUser(isSubtask ? 'editSubtask' : 'editTask', taskCreatedBy);
    const canCreateTask = useCanUser(isSubtask ? 'createSubtask' : 'createTask');
    const canRemoveTask = useCanUser(isSubtask ? 'removeSubtask' : 'removeTask', taskCreatedBy);
    const [isDependant] = useIsDependant(id);

    const actions = useMemo(() => {
        return (allowedActions || DEFAULT_ACTIONS).filter(action => {
            switch (action) {
                case 'addTimeEntry':
                    return !isSubtask && canAddTimeEntry;

                case 'viewTimeReport':
                    return !isSubtask && canViewTimeReport;

                case 'editName':
                    return canEditTask && !taskCompleted && Boolean(onEditNameClick);

                case 'changeMilestone':
                    return canEditTask && !taskCompleted && !isSubtask && projectId;

                case 'taskDep':
                    return canEditTask && !taskCompleted && !isSubtask && projectId;

                case 'taskLink':
                    return canEditTask && !taskCompleted && !isSubtask && projectId;

                case 'tieToGoal':
                    return canEditTask && !taskCompleted && !isSubtask && projectId;

                case 'dueDate':
                    return canEditTask && !taskCompleted;

                case 'labels':
                    return canEditTask && !taskCompleted && !isPersonal;

                case 'changeTaskGroup':
                    return canEditTask && !taskCompleted && !isSubtask && !isDependant;

                case 'duplicate':
                    return canCreateTask && !isSubtask;

                case 'createTemplate':
                    return canCreateTask && !isSubtask;

                case 'convertToTask':
                    return canEditTask && !taskCompleted && isSubtask;

                case 'moveToTask':
                    return canEditTask && !taskCompleted && isSubtask;

                case 'copyURL':
                    return !isSubtask && !isPersonal;

                case 'archive':
                    return canEditTask && !isSubtask && !isPersonal;

                case 'remove':
                    return canRemoveTask;

                default:
                    return true;

            }
        });
    }, [allowedActions, isSubtask, isPersonal, taskCompleted, projectId, canViewTimeReport, Boolean(onEditNameClick), isDependant, canCreateTask, canCreateTask, canRemoveTask]);

    const hidePopout = useCallback(() => {
        popoutCtrl.current?.hide();
    }, [popoutCtrl]);

    const handleTriggerClick = () => {
        popoutCtrl.current?.show();
    };

    const handleShow = useCallback(() => {
        setVisible(true);
    }, []);

    const handleHide = useCallback(() => {
        setVisible(false);
    }, []);

    const handleAddTimeClick = useCallback(() => {
        hidePopout();
        closeFlyout();

        requestAnimationFrame(() => {
            openFlyout('timeTracking', {
                taskId: id,
                project: projectId,
                association: 'task',
                addingTime: true
            });
        });
    }, [id, projectId, hidePopout]);

    const handleViewTimeLogClick = useCallback(() => {
        hidePopout();
        closeFlyout();

        requestAnimationFrame(() => {
            openFlyout('timeTracking', {
                taskId: id,
                project: projectId,
                association: 'task'
            });
        });
    }, [id, projectId, hidePopout]);

    const handleEditNameClick = useCallback(() => {
        onEditNameClick && onEditNameClick();

        hidePopout();
    }, [onEditNameClick, hidePopout]);

    const handleMilestoneSelect = useCallback(({ id: milestone }) => {
        updateTask(id, { milestone });

        hidePopout();
    }, [id, hidePopout]);

    const handleMilestoneCreate = useCallback(() => {
        hidePopout();
    }, [hidePopout]);

    const handleMilestoneUntie = useCallback(() => {
        if (!taskMilestone) {
            return;
        }

        updateTask(id, { milestone: null });

        hidePopout();
    }, [id, taskMilestone, hidePopout]);


    const handleDepSelect = useCallback(dependency => {
        updateTask(id, { dependency });

        hidePopout();
    }, [id, hidePopout]);

    const handleTaskUntie = useCallback(() => {
        if (!taskDep) {
            return;
        }

        updateTask(id, { dependency: null });

        hidePopout();
    }, [id, taskDep, hidePopout]);

    const handleLinkTaskSelect = useCallback(otherTaskId => {
        linkTasks(id, [otherTaskId]);

        hidePopout();
    }, [id, hidePopout]);

    const handleTieToGoalSelect = useCallback(({ id: goalId }) => {
        tieTasks(goalId, [id]);

        hidePopout();
    }, [id, hidePopout]);

    const handleDueDateRemove = useCallback(() => {
        updateTask(id, { due_date: null });

        hidePopout();
    }, [id, hidePopout]);

    const handleMoveConfirm = useCallback(async ({ id: nextTaskGroupId, projectId: nextProjectId }) => {
        try {
            if (nextProjectId && nextProjectId !== projectId) {
                await updateTask(id, {
                    project: nextProjectId,
                    labels: [],
                    assignees: [],
                    milestone: null,
                    order: Number.POSITIVE_INFINITY
                }, true);
            }

            await moveTask(id, {
                type: 'task_group',
                id: nextTaskGroupId
            });

            createAlert('success', `<i class="icon material-icons">check_circle</i> ${t('Successfully moved task')}`);
        } catch (e) {}

        hidePopout();
    }, [id, projectId, hidePopout]);

    const handleDuplicateConfirm = useCallback(async ({ id: nextTaskGroupId }) => {
        try {
            await duplicateTask(id, nextTaskGroupId, isPersonal);

            createAlert('success', `<i class="icon material-icons">check_circle</i> ${t('alerts.taskDuplicated')}`);
        } catch (e) {}

        hidePopout();
    }, [id, isPersonal, hidePopout]);

    const handleCreateTemplateClick = useCallback(() => {
        showModal('CREATE_TEMPLATE', {
            resourceId: id,
            name: taskName,
            type: 'task',
            fields: [
                { type: 'due_date', value: taskDueDate || null },
                { type: 'start_date', value: taskStartDate || null }
            ].filter(Boolean),
            disabledOptions: [
                !taskDescription ? 'description' : null,
                !taskLabels?.length ? 'labels' : null
            ].filter(Boolean)
        });

        hidePopout();
    }, [id, taskName, taskDescription, taskStartDate, taskDueDate, taskLabels, hidePopout]);

    const handleConvertToTaskConfirm = useCallback(({ id: nextTaskGroupId }) => {
        updateTask(id, {
            task_group: nextTaskGroupId,
            task: null,
            order: Number.POSITIVE_INFINITY
        });

        hidePopout();
    }, [id, hidePopout]);

    const handleMoveToTaskConfirm = useCallback(nextTaskId => {
        updateTask(id, {
            task: nextTaskId
        });

        hidePopout();
    }, [id, hidePopout]);

    const handleCopyUrlClick = useCallback(() => {
        trackEvent('TASK.URL_COPY', {
            via: 'Task'
        });

        copyUrlToClipboard(buildAppUrl(`/${projectId}/task/${id}-${createSlug(taskName)}`));

        hidePopout();
    }, [id, projectId, taskName, hidePopout]);

    const handleArchiveClick = useCallback(() => {
        trackEvent('TASK.ARCHIVE');

        updateTask(id, {
            archived: true,
            archived_on: new Date()
        });

        hidePopout();
    }, [id, hidePopout]);

    const handleUnarchiveClick = useCallback(() => {
        trackEvent('TASK.UNARCHIVE');

        updateTask(id, { archived: false });

        hidePopout();
    }, [id, hidePopout]);

    const handleDeleteClick = useCallback(() => {
        removeTask(id);

        onRemove && onRemove();

        hidePopout();
    }, [id, hidePopout, onRemove]);

    const renderAction = useCallback((actionId, onExpand, onCollapse) => {
        switch (actionId) {
            case 'addTimeEntry':
                return (
                    <div className="item-inner" onClick={handleAddTimeClick}>
                        <span className="item-icon"><IconClock /></span>

                        { t('time-tracking:manualEntry.addButtonText') }
                    </div>
                );

            case 'viewTimeReport':
                return (
                    <div className="item-inner" onClick={handleViewTimeLogClick}>
                        <span className="item-icon"><IconClock /></span>

                        { t('time-tracking:View time log') }
                    </div>
                );

            case 'editName':
                return (
                    <div className="item-inner" onClick={handleEditNameClick}>
                        <span className="item-icon"><IconEdit /></span>

                        { t('actions.Edit task name') }
                    </div>
                );

            case 'changeMilestone':
                return (
                    <SelectMilestonePopout
                        title={ t('actions.changeList.title') }
                        projectId={projectId}
                        selected={taskMilestone}
                        showCreate={true}
                        inline={true}
                        isList={true}
                        xAlign="right"
                        defaultXPosition="left"
                        onSelect={handleMilestoneSelect}
                        onCreate={handleMilestoneCreate}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconList /></span>

                            { t('actions.changeList.title') }
                        </div>
                    </SelectMilestonePopout>
                );

            case 'taskDep':
                return (
                    <SelectTaskPopout
                        title={ t('actions.Set Task Dependency') }
                        excluded={[id]}
                        projectId={projectId}
                        selected={taskDep}
                        inline={true}
                        xAlign="right"
                        defaultXPosition="left"
                        onSelect={handleDepSelect}
                        onUntie={handleTaskUntie}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconTask /></span>

                            { t('actions.Set Task Dependency') }
                        </div>
                    </SelectTaskPopout>
                );

            case 'taskLink':
                return (
                    <SelectTaskPopout
                        title={ t('actions.linkTask.title') }
                        excluded={[id]}
                        projectId={projectId}
                        inline={true}
                        xAlign="right"
                        defaultXPosition="left"
                        onSelect={handleLinkTaskSelect}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconLink /></span>

                            { t('actions.linkTask.title') }
                        </div>
                    </SelectTaskPopout>
                );

            case 'tieToGoal':
                return (
                    <SelectGoalPopout
                        title={ t('actions.tieToGoal.title') }
                        excluded={[id]}
                        inline={true}
                        xAlign="right"
                        defaultXPosition="left"
                        onSelect={handleTieToGoalSelect}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconGoal /></span>

                            { t('actions.tieToGoal.title') }
                        </div>
                    </SelectGoalPopout>
                );

            case 'dueDate':
                return (
                    <TaskDueDatePicker
                        id={id}
                        xAlign="right"
                        defaultXPosition="left"
                        showQuickOptions={true}
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconCalendarSimple style={{ marginTop: -1 }} /></span>

                            { t(`actions.dueDate.${!taskDueDate ? 'title' : 'titleChange'}`) }
                        </div>
                    </TaskDueDatePicker>
                );

            case 'labels':
                return (
                    <LabelPicker
                        defaultYPosition="top"
                        defaultXPosition="left"
                        taskId={id}
                        projectId={projectId}
                        assignedLabels={taskLabels}
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconLabel /></span>

                            { t('labels:Manage Tags') }
                        </div>
                    </LabelPicker>
                );

            case 'changeTaskGroup':
                return (
                    <SelectTaskGroupPopout
                        title={ t('actions.changeStatus.title') }
                        selected={taskTaskGroup}
                        projectId={projectId}
                        inline={true}
                        canSelectSame={false}
                        onShow={onExpand}
                        onHide={onCollapse}
                        onConfirm={handleMoveConfirm}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconStatus /></span>

                            { t('actions.changeStatus.title') }
                        </div>
                    </SelectTaskGroupPopout>
                );

            case 'duplicate':
                return (
                    <SelectTaskGroupPopout
                        title={ t('actions.duplicate.title') }
                        selected={taskTaskGroup}
                        projectId={projectId}
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                        onConfirm={handleDuplicateConfirm}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconPlus /></span>

                            { t('actions.duplicate.title') }
                        </div>
                    </SelectTaskGroupPopout>
                );

            case 'createTemplate':
                return (
                    <div className="item-inner" onClick={handleCreateTemplateClick}>
                        <span className="item-icon"><IconTemplate /></span>

                        { t('template:save') }
                    </div>
                );

            case 'convertToTask':
                return (
                    <SelectTaskGroupPopout
                        title={ t('actions.Convert Subtask to Task') }
                        selected={taskTaskGroup}
                        projectId={projectId}
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                        onConfirm={handleConvertToTaskConfirm}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconTask /></span>

                            { t('actions.Convert to a task') }
                        </div>
                    </SelectTaskGroupPopout>
                );

            case 'moveToTask':
                return (
                    <SelectTaskPopout
                        title={ t('actions.Move Subtask into another Task') }
                        excluded={[id]}
                        projectId={projectId}
                        selected={parentTaskId}
                        inline={true}
                        xAlign="right"
                        defaultXPosition="left"
                        onSelect={handleMoveToTaskConfirm}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconTransfer /></span>

                            { t('actions.Move into another task') }
                        </div>
                    </SelectTaskPopout>
                );

            case 'copyURL':
                return (
                    <div className="item-inner" onClick={handleCopyUrlClick}>
                        <span className="item-icon"><IconLink /></span>

                        { t('Copy task URL') }
                    </div>
                );

            case 'archive':
                return (
                    <TaskArchiveToggle
                        projectId={projectId}
                        archived={taskArchived}
                        onArchive={handleArchiveClick}
                        onUnarchive={handleUnarchiveClick}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconArchive /></span>

                            { taskArchived ? t('common:Unarchive') : t('common:Archive') }
                        </div>
                    </TaskArchiveToggle>
                );

            case 'remove':
                return (
                    <PromptPopout
                        xAlign="right"
                        defaultXPosition="left"
                        title={ t(`actions.${isSubtask ? 'removeSubtask' : 'remove'}.title`) }
                        text={ t(`actions.${isSubtask ? 'removeSubtask' : 'remove'}.description`) }
                        confirmText={ t('common:Delete') }
                        confirmClassName="btn-caution"
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner" onClick={handleDeleteClick}>
                            <span className="item-icon"><IconRemove style={{ marginTop: -1 }} /></span>

                            { t('common:Delete') }
                        </div>
                    </PromptPopout>
                );

            default:
                return null;
        }
    }, [t, projectId, taskMilestone, taskDep, taskLabels, taskTaskGroup, taskArchived, parentTaskId, isSubtask, handleAddTimeClick, handleViewTimeLogClick, handleEditNameClick, handleMilestoneSelect, handleMilestoneCreate, handleMilestoneUntie, handleDepSelect, handleTaskUntie, handleLinkTaskSelect, handleTieToGoalSelect, handleMoveConfirm, handleDuplicateConfirm, handleCreateTemplateClick, handleConvertToTaskConfirm, handleMoveToTaskConfirm, handleCopyUrlClick, handleArchiveClick, handleUnarchiveClick, handleDeleteClick]);

    if (!actions.length) {
        return null;
    }

    return (
        <div className={className + (visible ? ' visible' : '')} onClick={handleTriggerClick}>
            <Tooltip tooltip={t('common:More options')} withTooltipHover={false}>
                <span className={innerClassName}><IconMoreHoriz /></span>
            </Tooltip>

            <Popout
                className="xsmall"
                onShow={handleShow}
                onHide={handleHide}
                provideController={popoutCtrl}
             >
                <PopoutListActions actions={actions} renderAction={renderAction} updatePopoutPosition={popoutCtrl.current?.updatePosition} />
            </Popout>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getParentTask = makeGetTask();
    const getProject = makeGetProject();
    const getMilestone = makeGetMilestone();

    return ({ tasks, milestones, projects }, ownProps) => {
        const task = getTask(tasks.items, ownProps.id);
        const parentTaskId = task?.task;
        const parentTask = getParentTask(tasks.items, parentTaskId);
        const projectId = task?.project || parentTask?.project;
        const project = projectId && task?.milestone && getProject(projects.items, projectId);

        return {
            taskLabels: task?.labels,
            taskMilestone: task?.milestone,
            taskDescription: task?.description,
            taskStartDate: task?.start_date,
            taskDueDate: task?.due_date,
            taskDep: task?.dependency,
            taskName: task?.name,
            projectId,
            taskTaskGroup: task?.task_group,
            taskArchived: task?.archived,
            taskCreatedBy: task?.created_by,
            taskCompleted: task?.completed,
            parentTaskId,
            isPersonal: isPersonalTask(task),
            isSubtask: isSubtaskTask(task)
        };
    };
}

export default connect(makeMapStateToProps)(TaskActions);
